import { motion } from "framer-motion";
import { BookOpenCheck, Landmark, Users, Briefcase } from "lucide-react";
import { FadeUp, Eyebrow, SectionShell, EASE } from "./Reveal";
import { CORE_SERVICES, EXTENDED_SERVICES } from "../constants/site";

const ICONS = { bookkeeping: BookOpenCheck, taxation: Landmark, payroll: Users };

const CARD =
  "group relative rounded-3xl border border-line bg-card p-7 sm:p-8 overflow-hidden shadow-soft transition-all duration-500 hover:-translate-y-1 hover:border-torange/50 hover:shadow-lift";

const CardIcon = ({ id }) => {
  const Icon = ICONS[id] || Briefcase;
  return (
    <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-bluedark/10 to-torange/15 border border-line">
      <Icon size={22} className="text-torange" />
    </div>
  );
};

export default function Services() {
  const taxation = CORE_SERVICES.find((s) => s.id === "taxation");
  const bookkeeping = CORE_SERVICES.find((s) => s.id === "bookkeeping");
  const payroll = CORE_SERVICES.find((s) => s.id === "payroll");

  return (
    <SectionShell id="services" className="py-24 sm:py-32">
      <FadeUp>
        <Eyebrow>Core Services</Eyebrow>
        <h2 className="mt-5 max-w-2xl font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08] text-ink">
          Everything busy season throws at you, <span className="text-gradient">handled.</span>
        </h2>
      </FadeUp>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-5">
        <FadeUp className="md:col-span-8" data-testid="service-card-taxation">
          <div className={`${CARD} h-full`}>
            <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-bluze/10 blur-3xl" />
            <CardIcon id="taxation" />
            <h3 className="font-display text-2xl font-bold text-ink">{taxation.title}</h3>
            <p className="mt-3 max-w-lg text-sm sm:text-base text-slate-500 leading-relaxed">{taxation.desc}</p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {["1040", "1065", "1120", "1120-S", "Workpapers included", "E-file ready"].map((chip) => (
                <span key={chip} className="rounded-lg border border-line bg-paper px-3.5 py-2 font-mono text-xs text-slate-600">
                  {chip}
                </span>
              ))}
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.08} className="md:col-span-4" data-testid="service-card-bookkeeping">
          <div className={`${CARD} h-full`}>
            <CardIcon id="bookkeeping" />
            <h3 className="font-display text-xl sm:text-2xl font-bold text-ink">{bookkeeping.title}</h3>
            <p className="mt-3 text-sm sm:text-base text-slate-500 leading-relaxed">{bookkeeping.desc}</p>
          </div>
        </FadeUp>

        <FadeUp className="md:col-span-4" data-testid="service-card-payroll">
          <div className={`${CARD} h-full`}>
            <CardIcon id="payroll" />
            <h3 className="font-display text-xl sm:text-2xl font-bold text-ink">{payroll.title}</h3>
            <p className="mt-3 text-sm sm:text-base text-slate-500 leading-relaxed">{payroll.desc}</p>
          </div>
        </FadeUp>

        <FadeUp delay={0.08} className="md:col-span-8" data-testid="service-card-kpo-extended">
          <div className={`${CARD} h-full`}>
            <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-torange/10 blur-3xl" />
            <h3 className="font-display text-xl sm:text-2xl font-bold text-ink">Extended KPO Services</h3>
            <div className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {EXTENDED_SERVICES.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, x: -14 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.05, ease: EASE }}
                  className="flex gap-3"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-torange" />
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold text-ink">{s.title}.</span>{" "}
                    <span className="text-slate-500">{s.desc}</span>
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </FadeUp>
      </div>
    </SectionShell>
  );
}
